/**
 * Right pane — per-slide editing + slide management.
 *
 * Shape (top → bottom):
 *   - Deck-level meta (title, theme, version, audience).
 *   - Per-slide management row (duplicate / delete with confirm — PR3).
 *   - **Slide text** (ADR-002 Phase B): the active slide's title and the
 *     body's text runs surfaced as plain, labelled, live-editable fields —
 *     so editors read and change *copy* without wading through markup.
 *   - **Raw markup & advanced**: the previous per-layout form (raw
 *     ``bodyHtml`` textarea) + JSON view, kept behind a disclosure for
 *     power users.
 *
 * Everything saves through ``useSlideAutosave`` — the plain fields and the
 * raw/JSON views all write to the same draft, so they stay consistent
 * (debounced on change, flushed on blur, ETag/If-Match concurrency).
 */

import { useMemo } from "react";

import { LayoutDispatcher } from "../layouts/LayoutDispatcher";
import { type SaveState } from "../useSlideAutosave";
import type { Deck, Slide, SlideUpdatePayload } from "../types";
import { SlideActions } from "./SlideActions";
import {
  extractTextRuns,
  setTextRun,
  type TextRun,
} from "./Inspector.helpers";

type SetField = <K extends keyof SlideUpdatePayload>(
  key: K,
  value: SlideUpdatePayload[K],
) => void;

interface InspectorProps {
  deck: Deck;
  activeIdx: number;
  /**
   * The active slide's autosave surface, **lifted to ``DeckEditor``** so the
   * Phase C/D in-place hooks and the Inspector's plain-text fields share a
   * single writer per slide (ADR-002 §4 — no last-write-wins races between
   * the two panes). The draft is the source of truth while editing; edits
   * here and edits in the preview both call ``setField("bodyHtml", …)`` /
   * ``flush()`` on the same instance.
   */
  draft: Slide;
  setField: SetField;
  flush: () => void;
  saveState: SaveState;
  /** Called when the user duplicates the active slide (PR3). */
  onDuplicateSlide?(idx: number): void;
  /** Called when the user confirms deletion of the active slide (PR3). */
  onDeleteSlide?(idx: number): void;
  /** Disables slide-management buttons while a mutation is in flight. */
  slideManagementBusy?: boolean;
}

export function Inspector({
  deck,
  activeIdx,
  draft,
  setField,
  flush,
  saveState,
  onDuplicateSlide,
  onDeleteSlide,
  slideManagementBusy = false,
}: InspectorProps) {
  const slide = deck.deck_json.slides[activeIdx];
  const showActions =
    slide && (onDuplicateSlide || onDeleteSlide) ? true : false;

  return (
    <aside style={wrapperStyle} data-testid="inspector">
      <DeckMetaSection deck={deck} />
      {slide ? (
        <SlideEditSection
          activeIdx={activeIdx}
          draft={draft}
          setField={setField}
          flush={flush}
          saveState={saveState}
          actions={
            showActions ? (
              <SlideActions
                slideNumber={activeIdx + 1}
                slideTitle={stripHtml(slide.titleHtml)}
                onDuplicate={() => onDuplicateSlide?.(activeIdx)}
                onDelete={() => onDeleteSlide?.(activeIdx)}
                busy={slideManagementBusy}
                deleteDisabled={deck.deck_json.slides.length <= 1}
              />
            ) : null
          }
        />
      ) : (
        <p style={emptyStyle}>Pick a slide from the rail to inspect it.</p>
      )}
    </aside>
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&[a-zA-Z]+;/g, " ")
    .trim();
}

function DeckMetaSection({ deck }: { deck: Deck }) {
  return (
    <section style={sectionStyle} data-testid="inspector-deck-meta">
      <h3 style={sectionTitleStyle}>Deck</h3>
      <div style={kvGroupStyle}>
        <KV label="Title" value={deck.title} />
        <KV label="Slug" value={deck.slug} mono />
        <KV label="Theme" value={deck.theme} mono />
        <KV label="Version" value={String(deck.version)} mono />
        <KV label="Slides" value={String(deck.deck_json.slides.length)} mono />
        <KV label="Audience" value={deck.audience_class ?? "—"} mono />
        <KV label="Cache tag" value={deck.deck_json.cacheTag} mono />
      </div>
      {deck.audience_class === "customer_facing" && <IpSafetyBanner />}
    </section>
  );
}

function SlideEditSection({
  activeIdx,
  draft,
  setField,
  flush,
  saveState,
  actions,
}: {
  activeIdx: number;
  draft: Slide;
  setField: SetField;
  flush: () => void;
  saveState: SaveState;
  actions: React.ReactNode;
}) {
  return (
    <section
      style={sectionStyle}
      data-testid={`inspector-slide-${activeIdx}`}
    >
      <header style={slideHeaderStyle}>
        <h3 style={sectionTitleStyle}>Slide {activeIdx + 1}</h3>
        <SaveStatePill state={saveState} />
      </header>
      {actions}
      <SlideTextEditor draft={draft} setField={setField} flush={flush} />
      <details style={advancedStyle} data-testid="inspector-advanced">
        <summary style={advancedSummaryStyle}>Raw markup &amp; advanced</summary>
        <div style={{ paddingTop: "0.75rem" }}>
          <LayoutDispatcher draft={draft} setField={setField} flush={flush} />
        </div>
      </details>
    </section>
  );
}

/**
 * Plain, labelled, live-editable view of the slide's text — the Phase B
 * heart of the Inspector. Title first, then one field per text run in the
 * body. Edits to the title rewrite ``titleHtml``; edits to a body run
 * rewrite only that run inside ``bodyHtml`` (structure/attrs/figures
 * preserved by ``Inspector.helpers``).
 */
function SlideTextEditor({
  draft,
  setField,
  flush,
}: {
  draft: Slide;
  setField: SetField;
  flush: () => void;
}) {
  // Derive runs from the *draft* on every render so the plain fields stay
  // in lock-step with edits made in the raw/JSON view (single source of
  // truth = the autosave draft).
  const titleRuns = useMemo(
    () => extractTextRuns(draft.titleHtml),
    [draft.titleHtml],
  );
  const bodyRuns = useMemo(
    () => extractTextRuns(draft.bodyHtml),
    [draft.bodyHtml],
  );

  // The common case is a title that's a single text run (plain or lightly
  // marked-up). Render it as one obvious "Title" field; if a title somehow
  // carries multiple runs, fall back to per-run fields so nothing is lost.
  const soleTitleRun = titleRuns.length === 1 ? titleRuns[0] : undefined;
  const simpleTitle = titleRuns.length <= 1;

  return (
    <div data-testid="slide-text-editor">
      <FieldGroupLabel>Title</FieldGroupLabel>
      {simpleTitle ? (
        <PlainField
          aria-label="Slide title"
          value={soleTitleRun?.text ?? draft.titleHtml}
          placeholder="Slide title"
          testId="slide-text-title"
          onChange={(next) => {
            // No text node to target when the title is empty/plain — write
            // the raw value straight through so a blank title can be typed.
            if (soleTitleRun) {
              setField(
                "titleHtml",
                setTextRun(draft.titleHtml, soleTitleRun.path, next),
              );
            } else {
              setField("titleHtml", next);
            }
          }}
          onBlur={flush}
        />
      ) : (
        titleRuns.map((run) => (
          <RunField
            key={run.path}
            run={run}
            testIdPrefix="slide-text-title-run"
            onChange={(next) =>
              setField("titleHtml", setTextRun(draft.titleHtml, run.path, next))
            }
            onBlur={flush}
          />
        ))
      )}

      <FieldGroupLabel>Body text</FieldGroupLabel>
      {bodyRuns.length === 0 ? (
        <p style={noTextStyle} data-testid="slide-text-empty">
          This slide has no editable text (it may be image- or chart-only).
          Use <em>Raw markup &amp; advanced</em> below to edit its HTML.
        </p>
      ) : (
        bodyRuns.map((run) => (
          <RunField
            key={run.path}
            run={run}
            testIdPrefix="slide-text-body-run"
            onChange={(next) =>
              setField("bodyHtml", setTextRun(draft.bodyHtml, run.path, next))
            }
            onBlur={flush}
          />
        ))
      )}
    </div>
  );
}

/**
 * One body/title text run as a labelled, auto-growing field. Short runs
 * use an ``<input>``; longer ones a ``<textarea>`` so paragraphs are
 * comfortable to edit.
 */
function RunField({
  run,
  testIdPrefix,
  onChange,
  onBlur,
}: {
  run: TextRun;
  testIdPrefix: string;
  onChange(next: string): void;
  onBlur(): void;
}) {
  const multiline = run.text.length > 60 || run.text.includes("\n");
  const testId = `${testIdPrefix}-${run.path}`;
  return (
    <label style={fieldLabelStyle}>
      <span style={fieldLabelTextStyle}>{run.label}</span>
      {multiline ? (
        <textarea
          value={run.text}
          rows={Math.min(8, Math.max(2, Math.ceil(run.text.length / 48)))}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={{ ...fieldInputStyle, resize: "vertical" }}
          data-testid={testId}
        />
      ) : (
        <input
          type="text"
          value={run.text}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          style={fieldInputStyle}
          data-testid={testId}
        />
      )}
    </label>
  );
}

/** A bare title field (used for the common single-run / empty title case). */
function PlainField({
  value,
  placeholder,
  testId,
  onChange,
  onBlur,
  "aria-label": ariaLabel,
}: {
  value: string;
  placeholder?: string;
  testId?: string;
  onChange(next: string): void;
  onBlur(): void;
  "aria-label"?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      style={{ ...fieldInputStyle, marginBottom: "0.6rem" }}
      data-testid={testId}
    />
  );
}

function FieldGroupLabel({ children }: { children: React.ReactNode }) {
  return <div style={groupLabelStyle}>{children}</div>;
}

function SaveStatePill({ state }: { state: SaveState }) {
  switch (state.status) {
    case "idle":
      return null;
    case "saving":
      return (
        <span style={pillStyle("var(--gray-9)", "var(--gray-3)")} data-testid="save-state-saving">
          Saving…
        </span>
      );
    case "saved":
      return (
        <span style={pillStyle("var(--green-11)", "var(--green-3)")} data-testid="save-state-saved">
          Saved
        </span>
      );
    case "conflict":
      return (
        <span
          style={pillStyle("var(--amber-12)", "var(--amber-3)")}
          title={state.message}
          data-testid="save-state-conflict"
        >
          Conflict
        </span>
      );
    case "error":
      return (
        <span
          style={pillStyle("var(--red-11)", "var(--red-3)")}
          title={state.message}
          data-testid="save-state-error"
        >
          Save failed
        </span>
      );
  }
}

function IpSafetyBanner() {
  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.5rem 0.75rem",
        border: "1px solid var(--amber-7)",
        background: "var(--amber-3)",
        color: "var(--amber-12)",
        borderRadius: 4,
        fontSize: "0.85em",
      }}
      data-testid="ip-safety-banner"
    >
      <strong>Customer-facing audience.</strong> The IP-safety gate (#13130)
      will inspect every slide on render. Violations are logged in warn mode
      today; once enforcement is on, render will refuse decks that mix
      restricted sources with this audience class.
    </div>
  );
}

function KV({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div style={kvRowStyle}>
      <span style={kvLabelStyle}>{label}</span>
      <span
        style={{
          ...kvValueStyle,
          fontFamily: mono ? "monospace" : "inherit",
        }}
      >
        {value}
      </span>
    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  width: 380,
  flex: "0 0 380px",
  borderLeft: "1px solid var(--gray-5)",
  background: "var(--gray-1)",
  overflowY: "auto",
};

const sectionStyle: React.CSSProperties = {
  padding: "1rem",
  borderBottom: "1px solid var(--gray-5)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "0.85em",
  fontWeight: 600,
  color: "var(--gray-11)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const slideHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: "0.75rem",
};

const emptyStyle: React.CSSProperties = {
  color: "var(--gray-9)",
  padding: "1rem",
};

const kvGroupStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.1rem",
};

const kvRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "0.5rem",
  padding: "0.2rem 0",
  fontSize: "0.9em",
};

const kvLabelStyle: React.CSSProperties = {
  flex: "0 0 110px",
  color: "var(--gray-10)",
  fontSize: "0.8em",
};

const kvValueStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// ── Slide-text editor styles ────────────────────────────────────────────────

const groupLabelStyle: React.CSSProperties = {
  margin: "0.25rem 0 0.5rem",
  fontSize: "0.72em",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--gray-10)",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  marginBottom: "0.6rem",
};

const fieldLabelTextStyle: React.CSSProperties = {
  color: "var(--gray-10)",
  fontSize: "0.72em",
  marginBottom: "0.2rem",
};

const fieldInputStyle: React.CSSProperties = {
  padding: "0.45rem 0.55rem",
  border: "1px solid var(--gray-7)",
  borderRadius: 4,
  background: "var(--gray-1)",
  color: "var(--gray-12)",
  fontSize: "0.95em",
  lineHeight: 1.4,
  width: "100%",
  boxSizing: "border-box",
};

const noTextStyle: React.CSSProperties = {
  margin: "0 0 0.6rem",
  fontSize: "0.85em",
  color: "var(--gray-10)",
};

const advancedStyle: React.CSSProperties = {
  marginTop: "0.5rem",
  borderTop: "1px solid var(--gray-4)",
  paddingTop: "0.6rem",
};

const advancedSummaryStyle: React.CSSProperties = {
  cursor: "pointer",
  fontSize: "0.78em",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--gray-10)",
  userSelect: "none",
};

function pillStyle(
  color: string,
  background: string,
): React.CSSProperties {
  return {
    padding: "0.15rem 0.5rem",
    background,
    color,
    borderRadius: 999,
    fontSize: "0.7em",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 600,
  };
}
